import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Calendar as CalendarIcon, Clock, Loader2 } from 'lucide-react';
import { format, addDays, addWeeks, addMonths } from 'date-fns';
import { z } from 'zod';
import TimeSlotSelector from '@/components/TimeSlotSelector';
import { TimeSlot } from '@/lib/availabilityChecker';
import { BUSINESS_TIMEZONE, TIMEZONE_FULL_LABEL } from '@/lib/timezoneConfig';

interface ScheduleAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientData: {
    id: string;
    full_name: string;
    email: string;
    phone: string | null;
  };
}

const appointmentSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().optional(),
  service_type: z.string().min(1, 'Please select a service type'),
  appointment_type: z.string().min(1, 'Please select an appointment type'),
  preferred_date: z.string().min(1, 'Date is required'),
  preferred_time: z.string().min(1, 'Time slot is required'),
  preferred_timezone: z.string().optional().nullable(),
  notification_preference: z.string().min(1, 'Notification preference is required'),
  message: z.string().optional(),
});

export default function ScheduleAppointmentDialog({
  open,
  onOpenChange,
  clientData,
}: ScheduleAppointmentDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);

  // Form state
  const [name, setName] = useState(clientData.full_name);
  const [email, setEmail] = useState(clientData.email);
  const [phone, setPhone] = useState(clientData.phone || '');
  const [serviceType, setServiceType] = useState('');
  const [appointmentType, setAppointmentType] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState('');
  const [timezone, setTimezone] = useState(BUSINESS_TIMEZONE);
  const [notificationPref, setNotificationPref] = useState('email');
  const [message, setMessage] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('weekly');
  const [appointmentCount, setAppointmentCount] = useState(4);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(clientData.full_name);
      setEmail(clientData.email);
      setPhone(clientData.phone || '');
      setServiceType('');
      setAppointmentType('');
      setSelectedDate(undefined);
      setSelectedTime('');
      setTimezone('America/Chicago');
      setNotificationPref('email');
      setMessage('');
      setIsRecurring(false);
      setFrequency('weekly');
      setAppointmentCount(4);
    }
  }, [open, clientData]);

  // Update selectedTime when selectedSlot changes
  useEffect(() => {
    if (selectedSlot) {
      const timeStr = format(selectedSlot.startTime, 'h:mm a');
      setSelectedTime(timeStr);
    } else {
      setSelectedTime('');
    }
  }, [selectedSlot]);

  // Helper function to create scheduled timestamps from date/time strings
  const createScheduledTimestamps = (dateStr: string, timeStr: string, durationMinutes: number = 60) => {
    // Parse the time string (e.g., "12:00 PM")
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    // Create date in LOCAL timezone by parsing components
    const [year, month, day] = dateStr.split('-').map(Number);
    const scheduledStart = new Date(year, month - 1, day, hours, minutes, 0, 0);
    
    // Create end timestamp (default 60 min duration)
    const scheduledEnd = new Date(scheduledStart.getTime() + durationMinutes * 60000);
    
    return {
      scheduled_start_time: scheduledStart.toISOString(),
      scheduled_end_time: scheduledEnd.toISOString(),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast({
        title: 'Error',
        description: 'Please select a date',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      const formData = {
        name,
        email,
        phone: phone || null,
        service_type: serviceType,
        appointment_type: appointmentType,
        preferred_date: selectedDate.toISOString().split('T')[0],
        preferred_time: selectedTime,
        preferred_timezone: appointmentType === 'virtual' ? timezone : null,
        notification_preference: notificationPref,
        message: message || null,
      };

      // Validate
      appointmentSchema.parse(formData);

      // Check if selected time is after-hours
      const isAfterHours = selectedSlot?.requiresApproval || false;
      const approvalReason = isAfterHours 
        ? `After hours - ${format(selectedDate, 'EEEE')} ${selectedTime}` 
        : null;

      if (!isRecurring) {
        // Single appointment
        const timestamps = createScheduledTimestamps(formData.preferred_date, formData.preferred_time);
        
        const { error } = await supabase.from('appointments').insert({
          ...formData,
          ...timestamps,
          client_id: clientData.id,
          status: isAfterHours ? 'pending' : 'pending',
          requires_approval: isAfterHours,
          approval_reason: approvalReason,
        });

        if (error) throw error;

        toast({
          title: isAfterHours ? 'After-Hours Request Submitted' : 'Appointment Scheduled',
          description: isAfterHours 
            ? 'Your after-hours appointment request will require approval from Tiny Tax Lady'
            : 'Your appointment request has been submitted',
        });
      } else {
        // Recurring appointments
        const appointments = [];
        let currentDate = selectedDate;

        // Create parent appointment
        const parentTimestamps = createScheduledTimestamps(formData.preferred_date, formData.preferred_time);
        
        const { data: parent, error: parentError } = await supabase
          .from('appointments')
          .insert({
            ...formData,
            ...parentTimestamps,
            client_id: clientData.id,
            status: 'pending',
            is_recurring: true,
            recurrence_pattern: {
              frequency,
              count: appointmentCount,
            },
          })
          .select()
          .single();

        if (parentError) throw parentError;

        // Create child appointments
        for (let i = 1; i < appointmentCount; i++) {
          if (frequency === 'weekly') {
            currentDate = addWeeks(currentDate, 1);
          } else if (frequency === 'biweekly') {
            currentDate = addWeeks(currentDate, 2);
          } else if (frequency === 'monthly') {
            currentDate = addMonths(currentDate, 1);
          }

          const childTimestamps = createScheduledTimestamps(
            currentDate.toISOString().split('T')[0], 
            formData.preferred_time
          );
          
          appointments.push({
            ...formData,
            ...childTimestamps,
            client_id: clientData.id,
            preferred_date: currentDate.toISOString().split('T')[0],
            status: 'pending',
            is_recurring: true,
            parent_appointment_id: parent.id,
          });
        }

        const { error: childError } = await supabase
          .from('appointments')
          .insert(appointments);

        if (childError) throw childError;

        toast({
          title: 'Recurring Appointments Scheduled',
          description: `${appointmentCount} appointments have been scheduled`,
        });
      }

      onOpenChange(false);
    } catch (error: any) {
      console.error('Error scheduling appointment:', error);
      
      // Handle Zod validation errors specifically
      if (error.name === 'ZodError') {
        const firstError = error.issues[0];
        const fieldName = firstError.path[0];
        
        // User-friendly field names
        const fieldLabels: Record<string, string> = {
          service_type: 'Service Type',
          appointment_type: 'Appointment Type',
          preferred_date: 'Date',
          preferred_time: 'Time Slot',
          name: 'Name',
          email: 'Email',
          notification_preference: 'Notification Preference',
        };
        
        const friendlyFieldName = fieldLabels[fieldName] || fieldName;
        
        toast({
          title: 'Missing Required Information',
          description: `Please fill in: ${friendlyFieldName}`,
          variant: 'destructive',
        });
        
        // Scroll to top of modal where Service Type and Appointment Type are
        const modalContent = document.querySelector('[role="dialog"] .overflow-y-auto');
        if (modalContent && (fieldName === 'service_type' || fieldName === 'appointment_type')) {
          modalContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        // Handle other errors (database, network, etc.)
        toast({
          title: 'Error',
          description: error.message || 'Failed to schedule appointment. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getRecurringPreview = () => {
    if (!isRecurring || !selectedDate) return null;
    const freqText = frequency === 'weekly' ? 'weekly' : frequency === 'biweekly' ? 'every 2 weeks' : 'monthly';
    return `This will create ${appointmentCount} appointments ${freqText} starting ${format(selectedDate, 'MMM d, yyyy')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule Appointment</DialogTitle>
          <DialogDescription>
            Fill out the form below to schedule your appointment
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="service-type">Service Type *</Label>
              <Select value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger id="service-type">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual Tax Return</SelectItem>
                  <SelectItem value="sole-proprietor">Sole Proprietor / Schedule C</SelectItem>
                  <SelectItem value="amended">Amended Return</SelectItem>
                  <SelectItem value="consultation">Tax Consultation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="appointment-type">Appointment Type *</Label>
              <Select value={appointmentType} onValueChange={setAppointmentType}>
                <SelectTrigger id="appointment-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-person">In-Person (Lenexa, KS)</SelectItem>
                  <SelectItem value="virtual">Virtual Meeting</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {appointmentType === 'virtual' && (
            <div className="space-y-2">
              <Label htmlFor="timezone">Your Timezone *</Label>
              <p className="text-xs text-muted-foreground">
                Note: All appointments are scheduled in {TIMEZONE_FULL_LABEL}. Select your timezone for reference.
              </p>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger id="timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern Time</SelectItem>
                  <SelectItem value="America/Chicago">Central Time</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Select Date *</Label>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                  }}
              className="rounded-md border"
            />
          </div>

          {selectedDate && (
            <div className="space-y-2">
              <TimeSlotSelector
                selectedDate={selectedDate}
                selectedSlot={selectedSlot}
                onSlotSelect={setSelectedSlot}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Notification Preference *</Label>
            <RadioGroup value={notificationPref} onValueChange={setNotificationPref}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="email" id="email-notif" />
                <Label htmlFor="email-notif" className="font-normal">Email</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="phone" id="phone-notif" />
                <Label htmlFor="phone-notif" className="font-normal">Phone</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="both" id="both-notif" />
                <Label htmlFor="both-notif" className="font-normal">Both</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="recurring"
                checked={isRecurring}
                onCheckedChange={(checked) => setIsRecurring(checked as boolean)}
              />
              <Label htmlFor="recurring" className="font-normal">
                Schedule recurring appointments
              </Label>
            </div>

            {isRecurring && (
              <div className="ml-6 space-y-3 border-l-2 pl-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="frequency">Frequency</Label>
                    <Select value={frequency} onValueChange={setFrequency}>
                      <SelectTrigger id="frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="count">Number of Appointments</Label>
                    <Select
                      value={appointmentCount.toString()}
                      onValueChange={(val) => setAppointmentCount(Number(val))}
                    >
                      <SelectTrigger id="count">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2, 3, 4, 5, 6, 8, 10, 12].map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {selectedDate && (
                  <p className="text-sm text-muted-foreground">{getRecurringPreview()}</p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Additional Notes</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Any additional information..."
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !selectedTime}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Schedule Appointment
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
